import { useRef, useState } from "react";
import LeafletMap, { MapLocation } from "../../../utilities/LeafletMap";
import styles from "./styles/MapBlock.module.scss";
import classNames from "classnames";
import { observer } from "mobx-react";
import LocationBlockItem from "./LocationBlockItem";
import { CSSTransition } from "react-transition-group";
import EditableField from "../../../utilities/EditableField";
import ApiClient from "../../../../backends/ApiClient";
import NotificationManager from "../../../notifications/NotificationManager";
import { runInAction } from "mobx";
import dynamic from "next/dynamic";
import { useStores } from "../../../../stores/useStores";

const EditDropDown = dynamic(() => import("../EditDropDown"));

const mapClassName = classNames({
  [styles.map]: true,
});
const mapBlockContainerClassName = classNames({
  [styles.mapBlockContainer]: true,
});
const organizationTitleClassName = classNames({
  [styles.organizationTitle]: true,
});
const mapInsertClassName = classNames({
  [styles.mapInsert]: true,
});

const transitionClassNames = {
  enter: styles.transitionEnter,
  enterActive: styles.transitionEnterActive,
  enterDone: styles.transitionEnterDone,
  exit: styles.transitionExit,
  exitActive: styles.transitionExitActive,
  exitDone: styles.transitionExitDone,
};

const MapBlock = observer(
  (props: { blockObj: IMapTerrace; aboveTheFold: boolean }) => {
    const { organizationStore, userStore, gridStore } = useStores();
    const [title, setTitle] = useState(props.blockObj.title);
    const [isEditing, setIsEditing] = useState(false);
    const transitionRef = useRef(null);

    const [currentExpandedLocation, setCurrentExpandedLocation] = useState<
      MapLocation | null | undefined
    >(() => {
      return (
        props.blockObj.locations.find(
          (l) =>
            l.organization.id === organizationStore.organization.id &&
            l.is_primary,
        ) ??
        props.blockObj.locations.find(
          (l) => l.organization.id === organizationStore.organization.id,
        )
      );
    });

    const toggleInsertOpen = (location: MapLocation | null = null) => {
      setCurrentExpandedLocation(location);
    };

    async function handleSave(newTitle: string) {
      const originalTitle = title;
      try {
        const client = await ApiClient();
        const updatedTerrace = await client.PATCH(
          "/organizations/{organization_pk}/terraces/{id}/",
          {
            params: {
              path: {
                organization_pk: props.blockObj.organization_id,
                id: props.blockObj.id,
              },
            },
            body: {
              title: newTitle,
            } as never,
          },
        );
        NotificationManager.success("Title successfully saved!");
        runInAction(() => {
          gridStore.blocks.set(props.blockObj.id, updatedTerrace);
        });
        setTitle(newTitle);
        setIsEditing(false);
      } catch {
        setTitle(originalTitle);
        NotificationManager.error("Something went wrong! Please try again.");
      }
    }

    function handleCancel() {
      setIsEditing(false);
    }

    return (
      <section className={mapBlockContainerClassName}>
        <div className={organizationTitleClassName}>
          {userStore.isEditor && (
            <EditDropDown
              title={title}
              blockObj={props.blockObj}
              className={styles.editDropDownMap}
            />
          )}
          <EditableField
            key="title"
            defaultValue={props.blockObj.title}
            isEditing={isEditing}
            onEditStart={() => setIsEditing(true)}
            onSave={handleSave}
            onCancel={handleCancel}
            canEdit={userStore.isEditor}
            placeholder="Enter a Title"
          >
            <h3 className={title}>
              {userStore.isEditor ? title || "Enter a Title" : title}
            </h3>
          </EditableField>
        </div>
        <div className={mapClassName}>
          <LeafletMap
            handleMarkerClick={toggleInsertOpen}
            locations={props.blockObj.locations}
            cluster={true}
            loadTiles={props.aboveTheFold}
          />
          {currentExpandedLocation && (
            <div className={styles.locationItemTransitionWrapper}>
              <CSSTransition
                nodeRef={transitionRef}
                appear
                classNames={transitionClassNames}
                in={props.aboveTheFold}
                timeout={500}
              >
                <div ref={transitionRef} className={mapInsertClassName}>
                  <LocationBlockItem
                    location={currentExpandedLocation}
                    onClick={() => toggleInsertOpen()}
                  />
                </div>
              </CSSTransition>
            </div>
          )}
        </div>
      </section>
    );
  },
);

export default MapBlock;
